/** @jsx isolateComponent */
import { forwardRef } from 'react';
import { pickBy, uniq } from 'lodash';
import {
  AnchorTarget,
  Animation,
  ColorName,
  Colors,
  useDesignSystem,
} from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

export type LinkProps = {
  /**
   * Indicates the element represents the current item within a set.
   */
  ariaCurrent?: 'page' | 'step' | 'location' | false;
  /**
   * Optional `aria-label` attribute to describe the `Link`.
   */
  ariaLabel?: string;
  /**
   * The content of the `Link`.
   */
  children: React.ReactNode;
  /**
   * The string provided by the `css` prop via emotion.
   * This should only ever be provided by the `css` prop!
   */
  className?: string;
  /**
   * The color of the `Link`.
   */
  color?: ColorName | undefined | null;
  /**
   * Where the `Link` links to.
   */
  href: string;
  /**
   * Optional `name` attribute allows consumers to create named anchor links.
   */
  name?: string;
  /**
   * Optional `blur` handler.
   */
  onBlur?: React.ReactEventHandler;
  /**
   * Optional `click` handler.
   */
  onClick?: React.MouseEventHandler;
  /**
   * Optional `focus` handler.
   */
  onFocus?: React.ReactEventHandler;
  /**
   * Optional `mousedown` handler.
   */
  onMouseDown?: React.MouseEventHandler;
  /**
   * Optional `mouseenter` handler.
   */
  onMouseEnter?: React.MouseEventHandler;
  /**
   * Optional `mouseleave` handler.
   */
  onMouseLeave?: React.MouseEventHandler;
  /**
   * Optional `mouseup` handler.
   */
  onMouseUp?: React.MouseEventHandler;
  /**
   * The `rel` attribute of the `Link`, which is the relationship of
   * the `Link` to its target.
   */
  rel?: string;
  /**
   * The `role` attribute of the `Link`, which specifies the function
   * of the `Link`.
   */
  role?: string;
  /**
   * The `target` attribute of the `Link`, which specifies how
   * the `Link` should open.
   */
  target?: AnchorTarget;
  /**
   * The `title` attribute of the `Link`, which provides additional
   * text describing the `Link`.
   */
  title?: string;
  /**
   * This represents any valid `data-*` attribute you may want to define
   */
  [dataAttribute: string]: any;
};

const Styles = labelStyles(__filename, {
  root: (props: LinkProps) => [
    {
      textDecoration: 'none',
      cursor: 'pointer',
      transition: `all ${Animation.transitionDuration} ease-in-out`,
    },
    props.color &&
      withModifier(props.color, {
        color: Colors[props.color],
      }),
  ],
});

// tries to guess if a link's href is external or not
// currently supports these domains (and any sub-domains)
const DOMAIN_WHITELIST = ['zapier.com', 'zapier-staging.com', 'localhost'];
const isLinkExternal = (href: string) => {
  if (!href || href.length === 0) {
    return false;
  }

  /*
   * This regex tests if an href needs to have a protocol prepended to it or not.
   * We must prepend the protocol for links that are missing it when needed (e.g. example.com),
   * otherwise URL's constructor defaults to using the baseUrl, reporting an incorrect `host`.
   * There are two cases where we know we *don't* need to add it, so we detect those here:
   *    1. starts with ?, /, #, or http
   *    2. a slash occurs before any periods (e.g. apps/foo, but not example.com/apps)
   */
  const matcher = /(^[?#/]|http)|^[^.]+(\/)/;
  let hrefWithProtocol = href;

  if (!matcher.test(href)) {
    hrefWithProtocol = `https://${href}`;
  }

  // default base url to zapier.com allowing `URL`'s constructor to parse relative/fragment links
  const baseUrl = 'https://zapier.com';
  const url = new URL(hrefWithProtocol, baseUrl);
  const [topLevelDomain, secondLevelDomain] = url.host
    .split(':')[0]
    .split('.')
    .reverse();
  const domain = secondLevelDomain
    ? `${secondLevelDomain}.${topLevelDomain}`
    : topLevelDomain;
  return !DOMAIN_WHITELIST.includes(domain);
};

const buildRel = (rel: string | undefined | null, isExternal: boolean) => {
  // Links to other websites should default to security best practices for the `rel` attribute
  // which fixes click-jacking vulnerabilities and other security/privacy issues
  // https://medium.com/@jitbit/target-blank-the-most-underestimated-vulnerability-ever-96e328301f4c
  // https://www.techwyse.com/blog/search-engine-optimization/what-you-need-to-know-about-rel-noreferrer-attribute/
  const externalLinkRel = ['noopener', 'noreferrer'];
  const propsRel = (rel && rel.split(/\s+/)) || []; // merge together any `rel` values passed in with any `rel` values required for external links

  return (
    uniq([...propsRel, ...(isExternal ? externalLinkRel : [])]).join(' ') ||
    undefined
  );
};

/**
 * Standard link component. Use only when routing somewhere!
 *
 * Tags: core-ui
 */

// Note about forcing `React.FC` type here:
// It would be more appropriate to let TypeScript infer the type
// but when generating the flow definition file it's a huge hassle.

// Note, using `function` keyword to facilitate naming the component
// since it's wrapped in `forwardRef`, and assigning `displayName` to
// the `Link` constant doesn't work.
const Link: React.FC<LinkProps> = forwardRef(function Link(
  props: LinkProps,
  ref: React.Ref<HTMLAnchorElement>
) {
  const trimmedHref = props.href ? props.href.trim() : props.href;
  const { LinkComponent } = useDesignSystem();

  const dataAttributes = pickBy(
    props,
    (_, key) => typeof key === 'string' && key.startsWith('data-')
  );
  return (
    <LinkComponent
      aria-current={props.ariaCurrent}
      aria-label={props.ariaLabel}
      className={props.className}
      css={Styles.root(props)}
      href={trimmedHref}
      name={props.name}
      onBlur={props.onBlur}
      onClick={props.onClick}
      onFocus={props.onFocus}
      onMouseDown={props.onMouseDown}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
      onMouseUp={props.onMouseUp}
      ref={ref}
      rel={buildRel(props.rel, isLinkExternal(props.href))}
      role={props.role}
      target={props.target}
      title={props.title}
      {...dataAttributes}
    >
      {props.children}
    </LinkComponent>
  );
});

Link.defaultProps = {
  color: 'blue500',
};

export default Link;
